import { useEffect, useState } from 'react'
import { Video } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import { useQuery } from '@tanstack/react-query'

import { Button } from '@/custolink-newdesign/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/custolink-newdesign/ui/tooltip'
import { uiNavigationState } from '@/custolink-state/ui_navigation_state'
import useUiContext from '@/custolink-hooks/useUiContext'
import { AdmSupportVideoCallsApi } from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'

/**
 * New-design visuals for custolink-components/navigation/IncomingVideoSupport.tsx.
 *
 * Same data source, unchanged: AdmSupportVideoCallsApi.getLiveSessions, polled
 * every 15s while uiNavigationState.incomingVideoChatPollNow is true. That flag
 * is flipped on by the admin-area websocket the moment a customer requests
 * video support (EnduserRequestsAssetVideoCall in UseWebsocketsForAdminArea.tsx)
 * and flipped off here again once nobody is waiting - exactly like the
 * component it replaces. Only the markup changes: a ghost icon button with a
 * pulsing dot (the same ping pattern pages/support/IncomingCalls.tsx:112-114
 * uses for a live call row) instead of the bare Ecme-styled link.
 */
export default function IncomingCallIndicator() {
  const navigate = useNavigate()
  const uic = useUiContext()
  const [uiNavState, setUiNavState] = useAtom(uiNavigationState)
  const [show, setShow] = useState(false)

  const liveSessions = useQuery({
    queryFn: () =>
      AdmSupportVideoCallsApi.getLiveSessions(uic.organisationContext.organisationId),
    queryKey: [
      'AdmSupportVideoCallsApi',
      AdmSupportVideoCallsApi.getLiveSessions.name,
      uic.organisationContext.organisationId,
    ],
    enabled: uiNavState.incomingVideoChatPollNow,
    refetchInterval: 15000,
  })

  useEffect(() => {
    // A customer sitting alone in a room is the thing that needs a person's attention -
    // that is what the old pending-call list meant, before there was no accept handshake.
    const waiting = (liveSessions.data ?? []).filter(
      (s) => s.customerPresent && !s.workerPresent
    )
    if (waiting.length > 0) {
      setShow(true)
    } else {
      setShow(false)
      setUiNavState((ex) => ({ ...ex, incomingVideoChatPollNow: false }))
    }
  }, [liveSessions?.data])

  if (!show) {
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Incoming video support request"
          className="cl:relative cl:text-primary cl:hover:text-primary cl:size-9 cl:md:size-8"
          onClick={() => navigate('/admin/communication/support')}
        >
          <Video />
          <span className="cl:absolute cl:top-1.5 cl:right-1.5 cl:flex cl:size-2">
            <span className="cl:bg-primary/70 cl:motion-safe:animate-ping cl:absolute cl:inline-flex cl:size-full cl:rounded-full cl:opacity-75" />
            <span className="cl:bg-primary cl:relative cl:inline-flex cl:size-2 cl:rounded-full" />
          </span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>Incoming video support request</TooltipContent>
    </Tooltip>
  )
}
